'use strict'
import * as React from 'react'
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom'
import { Link } from '@mui/material'
import { titleName, viewId } from '../../../_constants/Code'
import { useToast } from '../../../_components/Toast'
import { errorLog } from '../../../common/logger/Logger'
import { useConfirmationModal } from '../../../_components/Modal/useConfirmationModal'
import {
  useJudgmentRuleSettingDispatch,
  useJudgmentRuleSettingSelector,
  useJudgmentRuleSettingStore
} from './extra/store'
import {
  fetchJudgmentRuleSubData,
  fetchRule,
  createOrUpdateRule,
  buildFilteredGenericDataKindClassifications,
  getChrofyColumnsByDataClassificationCode
} from './extra/services'
import { getMessage } from '../../../common/utils/MessageUtils'
import { resolveResultDropdownOptions } from './extra/classificationField'
import { buildRegisterConfirmDescription, prepareJudgmentRuleRegisterRequest } from './extra/registerConfirm'
import PageLoading from '../../../_components/PageLoading'
import RuleList from './components/RuleList'
import ConditionSidebar from './components/ConditionSidebar'
import { SpinnerButton } from '../../common/CustomButton'
import { ChrofyCustomSelect } from '../../common/ChrofyCustomSelect'

/**
 * 判定ルール設定コンテンツコンポーネント
 *
 * @param {Object} props
 * @param {string} props.corporationNumber
 * @param {string} props.userId
 * @param {Object} props.authority
 * @param {string} props.pageTitle
 */
const JudgmentRuleSettingContent = (props) => {
  const store = useJudgmentRuleSettingStore()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const showToast = useToast()
  const openConfirmationModal = useConfirmationModal()

  const dispatch = useJudgmentRuleSettingDispatch()
  const {
    loading,
    errors,
    ruleName,
    validStartMonth,
    validEndMonth,
    periodKind,
    targetCategory,
    targetCategoryCode,
    targetField,
    targetFieldCode,
    judgmentRules,
    elseResult,
    mappingTaskClassifications,
    genericDataKinds,
    chrofyColumns,
    ruleStatus,
    rawDataClassifications,
    classificationItems
  } = useJudgmentRuleSettingSelector((state) => ({
    loading: state.loading,
    errors: state.errors,
    ruleName: state.ruleName,
    validStartMonth: state.validStartMonth,
    validEndMonth: state.validEndMonth,
    periodKind: state.periodKind,
    targetCategory: state.targetCategory,
    targetCategoryCode: state.targetCategoryCode,
    targetField: state.targetField,
    targetFieldCode: state.targetFieldCode,
    judgmentRules: state.judgmentRules,
    elseResult: state.elseResult,
    mappingTaskClassifications: state.mappingTaskClassifications,
    genericDataKinds: state.genericDataKinds,
    chrofyColumns: state.chrofyColumns,
    ruleStatus: state.ruleStatus,
    rawDataClassifications: state.rawDataClassifications,
    classificationItems: state.classificationItems
  }))

  // 判定結果の設定先：対象期間に応じてデータ分類を絞り込み（月次→monthly、四半期→quarter、年次→year）
  const targetMappingTaskClassifications = React.useMemo(
    () => buildFilteredGenericDataKindClassifications(genericDataKinds, periodKind),
    [genericDataKinds, periodKind]
  )

  const classificationItemsMissingMessage = React.useMemo(() => {
    if (!targetField) return ''
    const { missingClassificationItems } = resolveResultDropdownOptions(
      targetField,
      rawDataClassifications,
      classificationItems
    )
    if (!missingClassificationItems) return ''
    return `先に区分マスター設定（ユーザー設定）画面で、${targetField}を設定してください。`
  }, [targetField, rawDataClassifications, classificationItems])

  // Statusに"fix"が含まれている場合は編集不可
  const isReadOnly = Array.isArray(ruleStatus) && ruleStatus.includes('fix')

  // 初期値を保持（ルール名、有効期間、対象期間、データ分類、データ項目、判定ルール、その他結果）
  const initialValuesRef = React.useRef({
    ruleName: '',
    validStartMonth: '',
    validEndMonth: '',
    periodKind: '',
    targetCategory: '',
    targetCategoryCode: '',
    targetField: '',
    targetFieldCode: '',
    judgmentRules: [{ condition: '', result: '' }],
    elseResult: ''
  })

  // 判定結果の設定先：データ分類コードに応じたデータ項目をフィルタリング（chrofyColumns）
  const getFilteredColumns = (categoryCode) => getChrofyColumnsByDataClassificationCode(chrofyColumns, categoryCode)

  const handleRuleNameChange = (value) => {
    dispatch('SET_RULE_NAME', value)
  }

  const handleValidStartMonthChange = (value) => {
    dispatch('SET_VALID_START_MONTH', value)
  }

  const handleValidEndMonthChange = (value) => {
    dispatch('SET_VALID_END_MONTH', value)
  }

  const handleTargetCategoryChange = (categoryCode) => {
    const cat = targetMappingTaskClassifications.find((c) => c.DataClassificationCode === categoryCode)
    dispatch('SET_TARGET_CATEGORY', {
      category: cat?.DataClassificationName ?? '',
      categoryCode: categoryCode ?? ''
    })
  }

  const handleTargetFieldChange = (fieldCode) => {
    const columns = getFilteredColumns(targetCategoryCode)
    const col = columns.find((c) => c.ColumnCode === fieldCode)
    dispatch('SET_TARGET_FIELD', { field: col?.ColumnName ?? '', fieldCode: fieldCode ?? '' })
  }

  /** 判定ルール・ELSEに値が1つでもあるか */
  const hasJudgmentRulesOrElseContent = () => {
    const hasRuleContent = (judgmentRules || []).some(
      (r) => (r.condition && r.condition.trim()) || (r.result && r.result.trim())
    )
    const hasElse = elseResult != null && String(elseResult).trim() !== ''
    return hasRuleContent || hasElse
  }

  /** 対象期間プルダウン変更時：値が入っていれば確認してから変更し、判定ルールをクリア */
  const handlePeriodKindChange = async (newValue) => {
    if (newValue === periodKind) return
    if (hasJudgmentRulesOrElseContent()) {
      await openConfirmationModal({
        title: '対象期間を変更しますか？',
        description: '判定ルールの条件式・判定結果・ELSEの判定結果に設定されている値はクリアされます。よろしいですか？',
        confirmName: 'OK',
        onClick: () => {
          dispatch('SET_TARGET_PERIOD', newValue)
          dispatch('RESET_JUDGMENT_RULES', {
            judgmentRules: [{ condition: '', result: '' }],
            elseResult: ''
          })
        }
      })
    } else {
      dispatch('SET_TARGET_PERIOD', newValue)
    }
  }

  React.useEffect(() => {
    document.title = props.pageTitle
  }, [props.pageTitle])

  // 判定ルール設定画面への遷移時（URLのid/new変更時）に入力欄をいったん初期化してからデータ取得
  const skipNextInitialValueUpdateRef = React.useRef(false)
  const isInitializedRef = React.useRef(false)
  React.useEffect(() => {
    isInitializedRef.current = false
    skipNextInitialValueUpdateRef.current = true
    dispatch('PREPARE_LOAD')
    init()
  }, [searchParams])

  // 初期値の保存（データ読み込み完了後、初回のみ）
  // 遷移直後の同ティックでは store がまだ古いためスキップし、INIT 適用後の描画で新ルールの初期値を保存する
  React.useEffect(() => {
    if (skipNextInitialValueUpdateRef.current) {
      skipNextInitialValueUpdateRef.current = false
      return
    }
    if (!loading && !isInitializedRef.current) {
      // データ読み込み完了後、初期値を保存
      initialValuesRef.current = {
        ruleName: ruleName || '',
        validStartMonth: validStartMonth || '',
        validEndMonth: validEndMonth || '',
        periodKind:
          periodKind === 'yearly' || periodKind === 'year'
            ? 'yearly'
            : periodKind === 'quarter'
              ? 'quarter'
              : periodKind === 'monthly'
                ? 'monthly'
                : '',
        targetCategory: targetCategory || '',
        targetCategoryCode: targetCategoryCode || '',
        targetField: targetField || '',
        targetFieldCode: targetFieldCode || '',
        judgmentRules: JSON.parse(JSON.stringify(judgmentRules || [{ condition: '', result: '' }])),
        elseResult: elseResult || ''
      }
      isInitializedRef.current = true
    }
  }, [
    loading,
    mappingTaskClassifications.length,
    ruleName,
    validStartMonth,
    validEndMonth,
    periodKind,
    targetCategory,
    targetCategoryCode,
    targetField,
    targetFieldCode,
    judgmentRules,
    elseResult
  ])

  /** 初期化処理 */
  const init = async () => {
    try {
      dispatch('CLEAR_ERRORS')

      const ruleId = searchParams.get('id')
      const isNew = searchParams.get('new') === 'true'

      // データ分類・データ項目を get_judgment_rule_sub_data API で取得
      const {
        dataClassifications,
        rawDataClassifications,
        classificationItems,
        chrofyClassifications,
        chrofyClassificationItems,
        dataColumns,
        genericDataKinds,
        mappingTaskClassifications,
        chrofyColumns
      } = await fetchJudgmentRuleSubData(store, props)

      let rule = null
      if (ruleId && !isNew) {
        // 編集時は既存ルールを取得
        rule = await fetchRule(store, ruleId, props)
        if (!rule) {
          dispatch('SET_ERRORS', {
            server: ['ルールが見つかりません'],
            client: {}
          })
        }
      }

      dispatch('INIT', {
        dataClassifications,
        rawDataClassifications,
        classificationItems,
        chrofyClassifications,
        chrofyClassificationItems,
        dataColumns,
        genericDataKinds,
        mappingTaskClassifications,
        chrofyColumns,
        rule
      })

      // 存在しないルールIDで編集モードを開いた場合、INIT が errors をクリアするためエラーを再設定する
      if (!rule && ruleId && !isNew) {
        dispatch('SET_ERRORS', {
          server: ['ルールが見つかりません'],
          client: {}
        })
      }
    } catch (err) {
      errorLog('E000001', props.userId, viewId.judgmentRuleSetting, 'init', err)
      dispatch('SET_ERRORS', {
        server: ['データ取得エラーが発生しました'],
        client: {}
      })
      dispatch('SET_LOADING', false)
    }
  }

  /** IF02089 登録・更新 API を実行する */
  const executeRegister = async (request) => {
    try {
      await createOrUpdateRule(store, request, props)
      showToast('判定ルールを登録しました')
      navigate('/master/dataMapping/judgmentRuleList')
    } catch (err) {
      errorLog('E000001', props.userId, viewId.judgmentRuleSetting, 'executeRegister', err)

      // バックエンドが返す日本語メッセージ（配列）のみ採用。 英語の err.message は
      // 画面表示せず、既定の日本語メッセージ E011004 にフォールバックする（バグ #3228）。
      let errorMessages = [getMessage('E011004', '登録処理')]
      if (err.response?.data?.message && Array.isArray(err.response.data.message)) {
        errorMessages = err.response.data.message
      }

      dispatch('SET_ERRORS', {
        server: errorMessages,
        client: {}
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  /** 登録処理（バリデーション → 確認モーダル → API） */
  const handleSubmit = async () => {
    if (classificationItemsMissingMessage) return

    dispatch('CLEAR_ERRORS')

    const prepared = prepareJudgmentRuleRegisterRequest(store.getState(), searchParams)
    if (!prepared.ok) {
      dispatch('SET_ERRORS', {
        server: prepared.serverErrors,
        client: prepared.clientErrors
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const { request, isNew } = prepared

    await openConfirmationModal({
      title: '判定ルール設定登録の確認',
      description: buildRegisterConfirmDescription({
        isNew,
        dataKindName: targetCategory,
        chrofyColumnName: targetField
      }),
      confirmName: '実行',
      onClick: () => executeRegister(request)
    })
  }

  /** 判定ルール配列を比較する（深い比較） */
  const areJudgmentRulesEqual = (rules1, rules2) => {
    if (rules1.length !== rules2.length) {
      return false
    }
    for (let i = 0; i < rules1.length; i++) {
      if (rules1[i].condition !== rules2[i].condition || rules1[i].result !== rules2[i].result) {
        return false
      }
    }
    return true
  }

  const hasChanges =
    isInitializedRef.current &&
    (ruleName !== initialValuesRef.current.ruleName ||
      validStartMonth !== initialValuesRef.current.validStartMonth ||
      validEndMonth !== initialValuesRef.current.validEndMonth ||
      periodKind !== initialValuesRef.current.periodKind ||
      targetCategory !== initialValuesRef.current.targetCategory ||
      targetCategoryCode !== initialValuesRef.current.targetCategoryCode ||
      targetField !== initialValuesRef.current.targetField ||
      targetFieldCode !== initialValuesRef.current.targetFieldCode ||
      !areJudgmentRulesEqual(judgmentRules, initialValuesRef.current.judgmentRules) ||
      elseResult !== initialValuesRef.current.elseResult)

  const isSubmitDisabled = !hasChanges || !!classificationItemsMissingMessage

  /** 判定ルール（IF条件・判定結果・ELSE判定結果）に変更があるか。リセットボタンの活性化に使用 */
  const hasJudgmentRulesChanges =
    isInitializedRef.current &&
    (!areJudgmentRulesEqual(judgmentRules, initialValuesRef.current.judgmentRules) ||
      elseResult !== initialValuesRef.current.elseResult)

  /** リセットボタンを活性にするか。対象期間が初期値と異なり判定ルールが空のときは非活性（押しても空のままなので無効化） */
  const isResetButtonEnabled =
    !!periodKind &&
    hasJudgmentRulesChanges &&
    (periodKind === initialValuesRef.current.periodKind || hasJudgmentRulesOrElseContent())

  /**
   * 判定ルールを初期状態にリセット（確認モーダル表示後）。 対象期間が初期値と異なる場合は判定ルールを復元せず空にする（例:
   * 年次で読み込んだルールを月次に変更した状態でリセットした場合、[今年度.XXX]が月次のまま残る不整合を防ぐ）。
   */
  const handleResetJudgmentRules = async () => {
    await openConfirmationModal({
      title: '判定ルールの変更を取り消しますか？',
      description: '判定ルールが編集前の状態に戻ります。',
      confirmName: '戻す',
      onClick: () => {
        const initialPeriod = initialValuesRef.current.periodKind
        const periodUnchanged = periodKind === initialPeriod
        if (periodUnchanged) {
          dispatch('RESET_JUDGMENT_RULES', {
            judgmentRules: initialValuesRef.current.judgmentRules,
            elseResult: initialValuesRef.current.elseResult
          })
        } else {
          dispatch('RESET_JUDGMENT_RULES', {
            judgmentRules: [{ condition: '', result: '' }],
            elseResult: ''
          })
        }
      }
    })
  }

  /** キャンセル処理 */
  const handleCancel = async () => {
    if (hasChanges) {
      // 変更がある場合のみ確認モーダルを表示
      await openConfirmationModal({
        title: '入力内容を破棄しますか？',
        description: '入力内容を破棄して判定ルール一覧に戻ります。',
        confirmName: '破棄',
        onClick: () => {
          navigate('/master/dataMapping/judgmentRuleList')
        }
      })
    } else {
      // 変更がない場合はそのまま戻る
      navigate('/master/dataMapping/judgmentRuleList')
    }
  }

  if (loading) {
    return <PageLoading />
  }

  return (
    <React.Fragment>
      <main className="page-container">
        <section className="page-content">
          <div className="centered-container">
            {/* 上階層に戻る */}
            <div className="bg main">
              <div className="back-upper-level py-8 pl-8 border-b border-blue2-lighten-20">
                <Link
                  color="inherit"
                  variant="button"
                  underline="none"
                  component={RouterLink}
                  to="/master/dataMapping/judgmentRuleList"
                >
                  <span className="back-upper-level__link link--text hover:lighten-70 flex items-center">
                    <span className="icon-arrow-circle icon-32px mr-4 rotate-180 relative after:absolute after:bg-white after:w-full after:h-full after:rounded-full after:left-0 after:-z-10"></span>
                    <span className="text-h5 font-bold">{titleName.judgmentRuleList}画面に戻る</span>
                  </span>
                </Link>
              </div>
            </div>
            {/* コンテンツヘッダ */}
            <header className="page-header flex justify-center py-1">
              <h1 className="page-header__name text-h1 grey--text py-8">判定ルール設定</h1>
            </header>

            {/* コンテンツボディ */}
            <div className="page-body">
              {/* エラーメッセージ表示 */}
              {errors.server.length > 0 && (
                <div className="error-messages mb-4">
                  {errors.server.map((error, index) => (
                    <p key={index} className="red--text">
                      {error}
                    </p>
                  ))}
                </div>
              )}
              <div className="box-xy-bordered rounded-t-3xl rounded-b-2xl pt-10 pb-16 px-16 border-blue1">
                <h2 className="box-bordered-heading blue1 white--text text-h3 font-medium pt-2.5 pb-3 px-14 -mt-10 -mx-16 rounded-t-2xl">
                  判定ルール設定
                </h2>

                <p className="text-body text-h7 grey--text my-6">条件に応じて自動的に判定結果を設定します。</p>

                <div className="flex gap-6">
                  <div className="flex-1">
                    {/* 基本情報エリア */}
                    <div className="bg-white rounded-lg shadow-sm mb-5 border border-gray-300">
                      <div className="px-6 py-4 font-semibold flex items-center gap-2 text-base bg-[#e5e7eb] text-[#1f2937] rounded-t-lg">
                        📝 基本情報
                      </div>
                      <div className="p-6">
                        <div className="flex gap-4 items-end">
                          <div className="flex-1">
                            <label className="form-label">
                              ルール名 <span className="text-red-600">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-select border border-gray-300 rounded py-2.5 px-3 w-full text-sm"
                              value={ruleName}
                              onChange={(e) => handleRuleNameChange(e.target.value)}
                              placeholder="例: 等級変動判定ルール"
                              disabled={isReadOnly}
                            />
                          </div>
                          <div className="flex-[1.2] ml-4">
                            <label className="form-label">有効期間（月単位）</label>
                            <div className="flex gap-2 items-end">
                              <div className="flex-1">
                                <input
                                  type="month"
                                  className="form-select border border-gray-300 rounded py-2.5 px-3 w-full text-sm"
                                  value={validStartMonth}
                                  onChange={(e) => handleValidStartMonthChange(e.target.value)}
                                  disabled={isReadOnly}
                                />
                              </div>
                              <div className="px-2 text-gray-500 pb-2.5">～</div>
                              <div className="flex-1">
                                <input
                                  type="month"
                                  className="form-select border border-gray-300 rounded py-2.5 px-3 w-full text-sm"
                                  value={validEndMonth}
                                  onChange={(e) => handleValidEndMonthChange(e.target.value)}
                                  disabled={isReadOnly}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 判定結果の設定先エリア */}
                    <div className="bg-white rounded-lg shadow-sm mb-5 border border-gray-300">
                      <div className="px-6 py-4 font-semibold flex items-center gap-2 text-base bg-[#e5e7eb] text-[#1f2937] rounded-t-lg">
                        📋 判定結果の設定先
                      </div>
                      <div className="p-6">
                        <div className="flex gap-4 items-start">
                          <div className="flex-[0_0_180px]">
                            <label className="form-label">
                              対象期間 <span className="text-red-600">*</span>
                            </label>
                            <ChrofyCustomSelect
                              className="w-full min-w-0"
                              value={periodKind ?? ''}
                              onChange={(e) => handlePeriodKindChange(e.target.value)}
                              disabled={isReadOnly}
                              hasNoSelect={false}
                              placeholder="選択してください"
                              options={[
                                { label: '選択してください', value: '' },
                                { label: '月次', value: 'monthly' },
                                { label: '四半期', value: 'quarter' },
                                { label: '年次', value: 'yearly' }
                              ]}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="form-label">
                              データ分類 <span className="text-red-600">*</span>
                            </label>
                            <ChrofyCustomSelect
                              className="w-full min-w-0"
                              value={targetCategoryCode}
                              onChange={(e) => handleTargetCategoryChange(e.target.value)}
                              disabled={isReadOnly || !periodKind}
                              hasNoSelect={false}
                              placeholder="データ分類を選択してください"
                              options={[
                                { label: 'データ分類を選択してください', value: '' },
                                ...(targetMappingTaskClassifications ?? []).map((cat) => ({
                                  label: cat.DataClassificationName,
                                  value: cat.DataClassificationCode
                                }))
                              ]}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="form-label">
                              データ項目 <span className="text-red-600">*</span>
                            </label>
                            <ChrofyCustomSelect
                              className="w-full min-w-0"
                              value={
                                targetFieldCode ||
                                (targetField
                                  ? ((getFilteredColumns(targetCategoryCode) ?? []).find(
                                      (c) => c.ColumnName === targetField
                                    )?.ColumnCode ?? '')
                                  : '')
                              }
                              onChange={(e) => handleTargetFieldChange(e.target.value)}
                              disabled={!targetCategoryCode || isReadOnly || !periodKind}
                              hasNoSelect={false}
                              placeholder={
                                targetCategoryCode ? 'データ項目を選択してください' : '先にデータ分類を選択してください'
                              }
                              options={[
                                {
                                  label: targetCategoryCode
                                    ? 'データ項目を選択してください'
                                    : '先にデータ分類を選択してください',
                                  value: ''
                                },
                                ...(getFilteredColumns(targetCategoryCode) ?? []).map((col) => ({
                                  label: col.ColumnName,
                                  value: col.ColumnCode
                                }))
                              ]}
                            />
                            {classificationItemsMissingMessage ? (
                              <p className="mt-1 text-sm leading-5 red--text">{classificationItemsMissingMessage}</p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 判定ルールエリア */}
                    <RuleList
                      isReadOnly={isReadOnly}
                      isRuleInputDisabled={!!classificationItemsMissingMessage}
                      hasJudgmentRulesChanges={isResetButtonEnabled}
                      onResetJudgmentRules={handleResetJudgmentRules}
                    />
                  </div>

                  {/* サイドバー（編集不可時は非表示） */}
                  {!isReadOnly && (
                    <div className="w-[360px] flex-shrink-0 sticky top-5 max-h-[calc(100vh-120px)] overflow-y-auto">
                      <ConditionSidebar isReadOnly={isReadOnly || !!classificationItemsMissingMessage} />
                    </div>
                  )}
                </div>
              </div>

              {/* フッター */}
              <div className="content-footer my-8">
                <div className="border-t border-blue1" />
                <div className="footer_button_area mt-8">
                  <button type="button" className="footer-btn button_back" onClick={handleCancel}>
                    キャンセル
                  </button>
                  <SpinnerButton
                    name="登録"
                    className="footer-btn button_base"
                    onClick={handleSubmit}
                    disabled={isSubmitDisabled}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </React.Fragment>
  )
}

export default JudgmentRuleSettingContent
